import KeyboardTabOutlined from '@mui/icons-material/KeyboardTabOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import { Divider, Stack, Typography } from '@mui/material';
import { CleaningMission } from '@silbo/core';
import { ClockIcon, UsersOutlineIcon } from '@silbo/ui';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ago } from '../utils/elapsed';
import { describePlannedEntry } from '../utils/planned-entry';
import DetailRow from './DetailRow';
import IsolationSpecificities from './IsolationSpecificities';

type MissionInfoRowsProps = {
  mission: CleaningMission;
  userId?: string;
  now: Date;
};

const iconSx = { color: 'text.secondary' } as const;

const MissionInfoRows = ({ mission, userId, now }: MissionInfoRowsProps) => {
  const { t } = useTranslation('environmental-cleaning');
  const entryAt = mission.bed.nextSlotAt;
  const plannedEntry = describePlannedEntry(entryAt, now);
  const isEntryPast = !!entryAt && !plannedEntry;
  const agent = mission.cleaner
    ? { label: t('mission.cleanedByLabel'), id: mission.cleaner.id, name: mission.cleanerName }
    : mission.assignee
    ? { label: t('mission.reservedByLabel'), id: mission.assignee.id, name: mission.assigneeName }
    : undefined;
  const isolation = mission.isolationSpecificities;

  const entryValue = () => {
    if (!entryAt || !plannedEntry) {
      return t('mission.noPlannedEntryShort');
    }
    const time = format(entryAt, 'HH:mm');
    if (plannedEntry.kind === 'in') {
      return t('mission.entryIn', { duration: plannedEntry.duration });
    }
    if (plannedEntry.kind === 'tomorrow') {
      return t('mission.entryTomorrow', { time });
    }
    return t('mission.entryOn', { date: plannedEntry.date, time });
  };

  return (
    <Stack divider={<Divider />}>
      <DetailRow icon={<ShieldOutlined sx={iconSx} />} label={t('mission.isolation')}>
        {isolation && isolation.length > 0 ? (
          <IsolationSpecificities specificities={isolation} />
        ) : isolation ? (
          t('mission.noIsolation')
        ) : (
          t('mission.unknownIsolation')
        )}
      </DetailRow>
      <DetailRow icon={<ClockIcon sx={iconSx} />} label={t('mission.requestedLabel')}>
        {ago(t, mission.requestedAt, now)}
      </DetailRow>
      {!isEntryPast && (
        <DetailRow icon={<KeyboardTabOutlined sx={iconSx} />} label={t('mission.entryLabel')}>
          {entryValue()}
        </DetailRow>
      )}
      {agent && (
        <DetailRow icon={<UsersOutlineIcon sx={iconSx} />} label={agent.label}>
          {agent.id === userId ? (
            <Typography component="span" sx={{ font: 'inherit', color: 'primary.main' }}>
              {t('mission.you')}
            </Typography>
          ) : (
            agent.name
          )}
        </DetailRow>
      )}
    </Stack>
  );
};

export default MissionInfoRows;
